<template>
    <div class="map-container" :style="{width: '100%', height: mapHeight + 'px'}">
        <div id="map" data-nosnippet></div>
        <InputText
            autocomplete="off"
            id="map-search"
            type="text"
            v-if="loaded"
            :placeholder="$t('search_address')"
        />
        <div class="map-sidebar animate__animated animate__faster" :class="sidebarAnimationClass" v-if="selected_pos">
            <div id="close-map-sidebar" @click="closeSidebar">
                <i class="fal fa-times"></i>
            </div>
            <h3 class="p-mb-3">{{ selected_pos.storename }}</h3>
            <p class="p-mb-1 p-mt-0 p-text-secondary">{{ selected_pos.address }}</p>
            <p class="p-mb-3 p-mt-0 p-text-secondary">{{ selected_pos.zipcode }} {{ selected_pos.city }}</p>
            <div class="p-mb-3 p-mt-0" v-if="selected_pos.deposit && selected_pos.deposit > 0"><span class="p-text-bold">{{ $t('deposit') }}</span> : {{ selected_pos.deposit }}€</div>
            <div class="table-responsive p-mb-3" v-if="selected_pos.openinghours">
                <table class="table table-bordered table-centered table-striped p-mb-0">
                    <thead>
                    <tr>
                        <th>{{ $t('day') }}</th>
                        <th class="p-text-center">{{ $t('schedules') }}</th>
                    </tr>
                    </thead>
                    <tbody v-if="Object.keys(selected_pos.openinghours).length">
                    <tr v-for="(hours, day) in selected_pos.openinghours" :key="day">
                        <td>{{ $t(day) }}</td>
                        <td class="p-text-center">
                            <div v-for="hour in hours" :key="hour">{{ hour }}</div>
                        </td>
                    </tr>
                    </tbody>
                    <tbody v-else>
                    <tr>
                        <td colspan="2" class="p-text-center">{{ $t('no_opening_hours') }}</td>
                    </tr>
                    </tbody>
                </table>
            </div>
            <p class="p-mb-3 p-mt-0 p-text-secondary">(*) {{ $t('schedules_information_only') }}</p>
            <div v-if="navigation_url">
                <a :href="navigation_url" target="_blank" class="navigation-btn">
                    <i class="fal fa-route p-mr-2"></i>
                    {{ $t('route') }}
                </a>
            </div>
        </div>
        <div class="map-progress-bar" v-if="!loaded">
            <p v-if="!error_loading_data" class="p-mt-0 p-mb-2">{{ $t('map_loading') }}</p>
            <p v-else class="p-mt-0 p-mb-2">{{ $t('error_occured_while_loading_pos') }}</p>
            <ProgressBar :class="{'p-progressbar-eror': error_loading_data}" mode="indeterminate"/>
        </div>
    </div>
</template>

<script>
import {onMounted, onUnmounted, ref, reactive, computed} from 'vue';
import MarkerClusterer from '@googlemaps/markerclustererplus';
import ProgressBar from 'primevue/progressbar';
import { Loader } from 'google-maps';
import settings from '@/settings'
import InputText from "primevue/inputtext";
import axios from "axios";

export default {
    name: 'PublicMapView',
    components: {
        ProgressBar,
        InputText
    },
    setup() {
        let map = null;
        let cluster = null;
        let component = ref(null);
        let initial_position = null;
        let user_position = ref(null);
        let user_position_marker = null;
        const user_position_icon = require('@/assets/theme/images/marker_user_position.png');
        const marker_cluster_icon = 'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m';
        const mapHeight = ref(window.innerHeight);

        const api_url = settings.POS_PUBLIC_URL;
        const loaded = ref(false);
        const error_loading_data = ref(false);
        const data = reactive([]);
        let markers = [];

        let selected_marker = null;
        const selected_pos = ref(null);
        const sidebarAnimationClass = ref(null);
        const map_navigation_base_url = 'https://www.google.com/maps/dir/?api=1&';
        const marker_icon = require('@/assets/theme/images/marker.png');

        const navigation_url = computed(function () {
            if (user_position.value) {
                let origin = user_position.value.lat + ',' + user_position.value.lng;
                let destination = selected_pos.value.latitude + ',' + selected_pos.value.longitude;
                return  map_navigation_base_url + 'origin=' + origin +'&destination=' + destination;
            }
            return null;
        });

        function openPosDetail(pos) {
            selected_pos.value = pos;
            sidebarAnimationClass.value = 'animate__fadeInRightBig';
        }

        function closeSidebar() {
            sidebarAnimationClass.value = 'animate__fadeOutRightBig';
            setTimeout(function() {
                selected_pos.value = null;
                if (selected_marker) {
                    selected_marker.setAnimation(null);
                }
                selected_marker = null;
            }, 100);
        }

        function initMap() {
            const options = {
                center: {lat: 48.856614, lng: 	2.3522219},
                zoomControlOptions: { position: window.google.maps.ControlPosition.BOTTOM_LEFT },
                mapTypeControl: false,
                fullscreenControl: false,
                streetViewControl: false,
                styles: [{"featureType":"all","elementType":"geometry.fill","stylers":[{"weight":"0.50"}]},{"featureType":"all","elementType":"geometry.stroke","stylers":[{"color":"#9c9c9c"}]},{"featureType":"all","elementType":"labels.text","stylers":[{"visibility":"on"}]},{"featureType":"administrative","elementType":"all","stylers":[{"visibility":"on"}]},{"featureType":"administrative.neighborhood","elementType":"all","stylers":[{"weight":"0.01"},{"invert_lightness":true},{"visibility":"on"}]},{"featureType":"administrative.land_parcel","elementType":"all","stylers":[{"visibility":"on"}]},{"featureType":"landscape","elementType":"all","stylers":[{"color":"#f2f2f2"},{"visibility":"on"}]},{"featureType":"landscape","elementType":"geometry.fill","stylers":[{"color":"#ffffff"}]},{"featureType":"landscape.man_made","elementType":"geometry.fill","stylers":[{"color":"#ffffff"},{"visibility":"off"}]},{"featureType":"landscape.natural.terrain","elementType":"all","stylers":[{"weight":"0.01"},{"visibility":"off"}]},{"featureType":"poi","elementType":"all","stylers":[{"visibility":"on"}]},{"featureType":"poi.attraction","elementType":"all","stylers":[{"visibility":"off"},{"lightness":"-27"}]},{"featureType":"poi.business","elementType":"all","stylers":[{"visibility":"off"}]},{"featureType":"poi.government","elementType":"all","stylers":[{"visibility":"on"}]},{"featureType":"poi.park","elementType":"all","stylers":[{"visibility":"on"}]},{"featureType":"poi.place_of_worship","elementType":"all","stylers":[{"visibility":"off"}]},{"featureType":"poi.school","elementType":"all","stylers":[{"visibility":"off"}]},{"featureType":"poi.sports_complex","elementType":"all","stylers":[{"visibility":"off"}]},{"featureType":"road","elementType":"all","stylers":[{"saturation":-100},{"lightness":45}]},{"featureType":"road","elementType":"geometry.fill","stylers":[{"color":"#eeeeee"}]},{"featureType":"road","elementType":"labels.text.fill","stylers":[{"color":"#7b7b7b"}]},{"featureType":"road","elementType":"labels.text.stroke","stylers":[{"color":"#ffffff"}]},{"featureType":"road.highway","elementType":"all","stylers":[{"visibility":"simplified"}]},{"featureType":"road.arterial","elementType":"labels.icon","stylers":[{"visibility":"off"}]},{"featureType":"transit","elementType":"all","stylers":[{"visibility":"off"}]},{"featureType":"transit.station","elementType":"all","stylers":[{"visibility":"on"}]},{"featureType":"water","elementType":"labels.text.fill","stylers":[{"color":"#070707"}]},{"featureType":"water","elementType":"labels.text.stroke","stylers":[{"color":"#ffffff"}]}],
                zoom: 8
            }
            map = new window.google.maps.Map(document.getElementById('map'), options);
            cluster = new MarkerClusterer(map, [], {imagePath: marker_cluster_icon});
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(function (position) {
                    user_position.value = {lat:position.coords.latitude, lng:position.coords.longitude};
                    initial_position = user_position.value;
                    map.setCenter(user_position.value);
                    map.setZoom(12);
                    user_position_marker = new window.google.maps.Marker({
                        position: user_position.value,
                        icon: user_position_icon,
                        map: map,
                    });
                }, function(error) {
                    console.log(error);
                });
            }
            else {
                console.log('geolocation not available');
            }
            getData(api_url);
        }

        function initSearchBox() {
            const input = document.getElementById("map-search");
            if (input) {
                const searchBox = new window.google.maps.places.SearchBox(input);
                searchBox.addListener("places_changed", () => {
                    const places = searchBox.getPlaces();
                    if (!places.length) {
                        return;
                    }
                    user_position.value = {
                        lat:places[0].geometry.location.lat(),
                        lng:places[0].geometry.location.lng()
                    };
                    if (selected_pos.value) {
                        closeSidebar();
                    }
                    map.setCenter(user_position.value);
                    map.setZoom(15);
                    if (user_position_marker) {
                        user_position_marker.setPosition(user_position.value);
                    }
                    else {
                        user_position_marker = new window.google.maps.Marker({
                            position: user_position.value,
                            icon: user_position_icon,
                            map: map,
                        });
                    }
                });
                input.addEventListener("keyup", event => {
                    if (!event.target.value) {
                        user_position.value = initial_position ? initial_position : null;
                        if (user_position_marker && user_position.value) {
                            user_position_marker.setPosition(user_position.value);
                        }
                        if (user_position.value) {
                            map.setCenter(user_position.value);
                        }
                    }
                });
            }
        }

        function getData(url) {
            axios({method: 'get', url: url}).then(
                (response) => {
                    data.push(...response.data.results);
                    if (response.data.next) {
                        getData(response.data.next);
                    }
                    else {
                        loadMarkers(data);
                    }
                },
                () => {
                    error_loading_data.value = true;
                }
            );
        }

        function loadMarkers(new_markers) {
            for (let i = 0; i < new_markers.length; i++) {
                let marker = new window.google.maps.Marker({
                    position: {lat:  new_markers[i].latitude, lng:  new_markers[i].longitude},
                    icon: marker_icon,
                    map: map
                });
                marker.addListener('click', function () {
                    if (selected_marker) {
                        selected_marker.setAnimation(null)
                    }
                    marker.setAnimation(window.google.maps.Animation.BOUNCE)
                    selected_marker = marker;
                    openPosDetail(data[i]);
                });
                markers.push(marker);
            }
            cluster.addMarkers(markers);
            loaded.value = true;
            setTimeout(function () {
                initSearchBox();
            }, 1000);
        }

        function resizeMap() {
            mapHeight.value = window.innerHeight;
        }

        onMounted(function() {
            if (!window.google) {
                let api_key = settings.PLATFORM.GG_API_KEY ? settings.PLATFORM.GG_API_KEY : settings.GG_API_KEY;
                const loader = new Loader(api_key, {
                    libraries: ['places'],
                });
                loader.load().then(function (google) {
                    window.google = google;
                    initMap();
                });
            }
            else {
                initMap();
            }
            window.addEventListener("resize", resizeMap);
        });

        onUnmounted(function () {
            window.removeEventListener("resize", resizeMap);
        });

        return {
            mapHeight,
            component,
            loaded,
            navigation_url,
            error_loading_data,
            selected_pos,
            sidebarAnimationClass,
            closeSidebar
        }
    }
}
</script>

<style lang="scss" scoped>
@import "src/assets/theme/scss/variable";
#map {
    width: 100%;
    height: 100%;
}
.map-container {
    position: relative;
}
.map-progress-bar {
    position: absolute;
    z-index: 1;
    width: 350px;
    background-color: white;
    overflow-y: auto;
    padding: 15px;
    background-clip: border-box;
    border: 0 solid #f7f7f7;
    box-shadow: 0 1px 3px 0 rgb(0 0 0 / 20%);
    color: var(--text-color);
    right: 10px;
    top: 10px;
}
.map-sidebar {
    width: 350px;
    height: 100%;
    position: absolute;
    z-index: 1;
    background-color: white;
    overflow-y: auto;
    padding: 15px;
    background-clip: border-box;
    border: 0 solid #f7f7f7;
    border-radius: .25rem;
    box-shadow: 0 1px 3px 0 rgb(0 0 0 / 20%);
    color: var(--text-color);
    right: 0;
    top: 0;
}
#close-map-sidebar {
    top: 0;
    right: 0;
    position: absolute;
    width: 50px;
    height: 50px;
    text-align: center;
    padding: 15px;
    font-size: 20px;
}
#close-map-sidebar i {
    cursor: pointer;
}
#close-map-sidebar i:hover {
    color: $secondary-color;
}
.navigation-btn {
    font-size: 14px;
    padding: 16px 24px 16px 24px;
    line-height: 20px;
    display: block;
    width: 100%;
    text-align: center;
    color: $button-text-color;
    border-radius: 4px;
    height: 50px;
    background-color: $button-color;
    border-color: $button-color;
}
.navigation-btn:hover {
    background-color: $button-hover-color;
    border-color: $button-hover-color;
}
#map-search {
    position: absolute;
    width: 350px;
    transition: width calc(var(--animate-duration) / 2) ease;
    top: 10px;
    left: 10px;
    z-index: 1;
}
@media (max-width: 430px) {
    #map-search {
        width: calc(100% - 20px) !important;
    }
}
</style>
